import React, { useState, useCallback, useEffect } from 'react';
import {
    Dialog, DialogActions, DialogContent, DialogTitle, Button, TextField, Checkbox, IconButton, Typography
} from '@mui/material';
import CasinoIcon from '@mui/icons-material/Casino';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';

import { getTextColor } from '../../utils/ColorUtils.jsx'
import ScrollAwareTooltip from 'components/Custom/ScrollAwareTooltip.jsx';


const LabelsComponent = ({
    open,
    handleClose,
    t,
    myBoardSortedLabels,
    selectedTaskLabels,
    labelAdding,
    labelEditing,
    toggleLabelSelection,
    handleEditClick,
    DeleteLabelModalOpen,
    handleSetLabelAddOpen,
    handleSetLabelAddClose,
    handleSaveLabel,
    handleSetCancelEdit,
    handleEditLabel,
    handleAddNewLabel,
    labelToEdit,
    handleEditingClose,
    setAutoFocusLabel
}) => {
    const [labelTitle, setLabelTitle] = useState('');
    const [labelDescription, setLabelDescription] = useState('');
    const [labelColor, setLabelColor] = useState('#000000');
    const [labelEditingTitle, setLabelEditingTitle] = useState(labelToEdit?.title || "");
    const [labelEditingDescription, setLabelEditingDescription] = useState(labelToEdit?.description || "");
    const [labelEditingColor, setLabelEditingColor] = useState(labelToEdit?.color || "#000000");
    const [labelEditingId, setLabelEditingId] = useState(labelToEdit?.id || "");

    useEffect(() => {
        if (labelToEdit) {
            setLabelEditingTitle(labelToEdit.title || "");
            setLabelEditingDescription(labelToEdit.description || "");
            setLabelEditingColor(labelToEdit.color || "#000000");
            setLabelEditingId(labelToEdit.id || "")
        }
    }, [labelToEdit]);

    const getRandomHex = () => {
        const letters = '0123456789ABCDEF';
        let color = '#';
        for (let i = 0; i < 6; i++) {
            color += letters[Math.floor(Math.random() * 16)];
        }
        return color;
    };

    const defaultColors = [
        "#CC0000",
        "#D21414",
        "#E63B3B",
        "#F46262",
        "#FF8888",
        "#FFAA00",
        "#FFBB33",
        "#FFCC66",
        "#FFDD99",
        "#FFEECC",
        "#FFFF00",
        "#CCFF33",
        "#99FF66",
        "#66FF99",
        "#33FFCC",
        "#00FFFF",
        "#33CCFF",
        "#6699FF",
        "#9966FF",
        "#CC33FF",
        "#FF00FF",
        "#FF33CC",
        "#FF6699",
        "#FF9966",
        "#FFCC33",
    ];

    const saveLabel = () => {
        setAutoFocusLabel(false)
        handleSaveLabel();
        setLabelTitle('');
        setLabelDescription('');
        setLabelColor('#000000');
    }

    const addLabel = (labelTitle, labelColor, close, labelDescription) => {
        setLabelTitle('');
        setLabelDescription('');
        setLabelColor('#000000');
        handleAddNewLabel(labelTitle, labelColor, close, labelDescription);
    };

    /* eslint-disable react-hooks/exhaustive-deps */
    const debouncedHandleColorSelection = useCallback((color) => {
        handleAddNewLabel(
            "",               // title, you can update this to be dynamic if needed
            color ? color : getRandomHex(),            // the selected color
            false,            // close flag, change to true if you want to close the modal
            ""                // description, update if needed
        );
    }, []);

    // Handler for saving the edited label
    const handleEditExistingLabel = (title, color, id, description) => {
        // Edit logic for an existing label (could call a prop function if needed)
        handleEditLabel(title, color, id, description);
    };

    return (
        <Dialog open={open} onClose={handleClose} className="view-task-label-dialog">
            {!labelAdding && !labelEditing && (
                <>
                    <DialogTitle className="view-task-label-title-width">{t("modal.label_select")}</DialogTitle>
                    <DialogContent>
                        <Typography variant="subtitle2">{t("modal.labels")}</Typography>
                        <div>
                            {myBoardSortedLabels?.map((label) => (
                                <div key={label.id} className="view-task-workers-list">
                                    <Checkbox
                                        checked={selectedTaskLabels?.some((item) => item.id === label.id)}
                                        onChange={() => toggleLabelSelection(label)}
                                        color="primary"
                                    />
                                    <div
                                        className={`view-task-label-list-style ${label.color === "rainbow" ? 'rainbow' : ''} 
                                        ${selectedTaskLabels?.some(item => item.id === label.id) ? 'selected' : ''}`}
                                        style={{ backgroundColor: label.color }}
                                    >
                                        <Typography variant="body2" style={{ color: getTextColor(label.color), fontWeight: "bold" }}  >
                                            {label.title}
                                        </Typography>
                                        {selectedTaskLabels?.some((item) => item.id === label.id) && <CheckIcon className="white-txt-color" />}
                                    </div>
                                    <IconButton onClick={() => handleEditClick(label)} size="small" className="view-task-label-list-icons">
                                        <EditIcon className="white-txt-color" />
                                    </IconButton>
                                    <IconButton onClick={() => DeleteLabelModalOpen(label.id)} size="small" className="view-task-label-list-icons">
                                        <DeleteIcon className="white-txt-color" />
                                    </IconButton>
                                </div>
                            ))}
                        </div>
                        <Button onClick={handleSetLabelAddOpen}>{t("modal.label_add")}</Button>
                        <ScrollAwareTooltip title={t("modal.label_random")}>
                            <CasinoIcon
                                className="white-txt-color"
                                onClick={() => debouncedHandleColorSelection()}
                            />
                        </ScrollAwareTooltip>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => saveLabel()}>{t("global.close")}</Button>
                    </DialogActions>
                </>
            )}

            {labelAdding && (
                <>
                    <DialogTitle className="view-task-add-label-actions">
                        {t("modal.label_add")}
                    </DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            margin="dense"
                            label={t("modal.label_title")}
                            type="text"
                            fullWidth
                            variant="standard"
                            value={labelTitle}
                            onChange={(e) => setLabelTitle(e.target.value)}
                            InputProps={{
                                sx: {
                                    
                                    backgroundColor: labelColor,
                                    color: getTextColor(labelColor),
                                    borderRadius: "8px",
                                    textAlign: 'center', 
                                    '& input': {
                                        textAlign: 'center',
                                    },
                                    '&:before': {
                                        borderBottom: 'none !important',
                                    },
                                    '&:after': {
                                        borderBottom: 'none !important',
                                    },
                                    '&:hover:not(.Mui-disabled):not(.Mui-focused):before': {
                                        borderBottom: 'none !important',
                                    },
                                }
                            }}
                        />
                        <TextField
                            margin="dense"
                            label={t("modal.label_desc")}
                            type="text"
                            fullWidth
                            variant="standard"
                            value={labelDescription}
                            onChange={(e) => setLabelDescription(e.target.value)}
                            InputProps={{
                                className: "view-task-label-title-input"
                            }}
                        />
                        <Typography className="view-task-label-title-input view-task-label-color-input">{t("modal.label_color")}</Typography>
                        <input
                            type="color"
                            value={labelColor !== "rainbow" ? labelColor : "#000000"}
                            onChange={(e) => setLabelColor(e.target.value)}
                            className={`view-task-label-color-picker ${labelColor === "rainbow" ? 'rainbow' : ''}`}
                            disabled={labelColor === "rainbow"}
                        />
                        {labelColor !== "rainbow" && (
                            <div className="view-task-label-colors">
                                {defaultColors.map((color, index) => (
                                    <div
                                        key={index}
                                        style={{ backgroundColor: color }}
                                        className="view-task-label-color-item"
                                        onClick={() => setLabelColor(color)}
                                    />
                                ))}
                            </div>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClose}>{t("global.cancel")}</Button>
                        <Button
                            onClick={() => addLabel(labelTitle, labelColor, true, labelDescription)}
                        >
                            {t("modal.label_save")}
                        </Button>
                    </DialogActions>
                </>
            )}

            {labelEditing && (
                <>
                    <DialogTitle className="view-task-add-label-actions">
                        {t("modal.label_edit")}
                    </DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            margin="dense"
                            label={t("modal.label_title")}
                            type="text"
                            fullWidth
                            variant="standard"
                            value={labelEditingTitle}
                            onChange={(e) => setLabelEditingTitle(e.target.value)}
                            InputProps={{
                                sx: {
                                    
                                    backgroundColor: labelEditingColor,
                                    color: getTextColor(labelEditingColor),
                                    borderRadius: "8px",
                                    textAlign: 'center', 
                                    '& input': {
                                        textAlign: 'center',
                                    },
                                    '&:before': {
                                        borderBottom: 'none !important',
                                    },
                                    '&:after': {
                                        borderBottom: 'none !important',
                                    },
                                    '&:hover:not(.Mui-disabled):not(.Mui-focused):before': {
                                        borderBottom: 'none !important',
                                    },
                                }
                            }}
                        />
                        <TextField
                            margin="dense"
                            label={t("modal.label_desc")}
                            type="text"
                            fullWidth
                            variant="standard"
                            value={labelEditingDescription}
                            onChange={(e) => setLabelEditingDescription(e.target.value)}
                            InputProps={{
                                className: "view-task-label-title-input"
                            }}
                        />
                        <Typography className="view-task-label-title-input view-task-label-color-input">{t("modal.label_color")}</Typography>
                        <input
                            type="color"
                            value={labelEditingColor !== "rainbow" ? labelEditingColor : "#000000"}
                            onChange={(e) => setLabelEditingColor(e.target.value)}
                            className={`view-task-label-color-picker ${labelEditingColor === "rainbow" ? 'rainbow' : ''}`}
                            disabled={labelEditingColor === "rainbow"}
                        />
                        {labelEditingColor !== "rainbow" && (
                            <div className="view-task-label-colors">
                                {defaultColors.map((color, index) => (
                                    <div
                                        key={index}
                                        style={{ backgroundColor: color }}
                                        className="view-task-label-color-item"
                                        onClick={() => setLabelEditingColor(color)}
                                    />
                                ))}
                            </div>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleEditingClose}>{t("global.cancel")}</Button>
                        <Button
                            onClick={() =>
                                handleEditExistingLabel(labelEditingTitle, labelEditingColor, labelEditingId, labelEditingDescription)
                            }
                        >
                            {t("global.save_changes")}
                        </Button>
                    </DialogActions>
                </>
            )}
        </Dialog>
    );
};

export default LabelsComponent;
